import * as Sentry from '@sentry/nextjs';
import Cookies from 'js-cookie';
import { createContext, useEffect, useRef, useState } from 'react';

import { SEGMENT_ANONYMOUS_ID_COOKIE_NAME } from '~/common/constants/user';
import {
  getSegmentAnonymousId,
  loadAnalytics,
  setWafTrafficMetadata,
  trackEvent,
} from '~/components/analytics/analytics';
import { BROWSER_AUTOMATION_SIGNALS_OBSERVED_EVENT_NAME } from '~/components/analytics/constants';
import {
  initializeTrustedInteractionTracking,
  setAnalyticsUserId,
} from '~/utils/botTrafficSignals';
import {
  getLikelyAutomatedBrowserEventProperties,
  isLikelyAutomatedBrowser,
} from '~/utils/likelyAutomatedBrowser';
import { EMPTY_WAF_TRAFFIC_METADATA } from '~/utils/wafSuspiciousTraffic';

import type { Analytics } from '@segment/analytics-next';
import type { ReactElement, ReactNode } from 'react';
import type { WafTrafficMetadata } from '~/utils/wafSuspiciousTraffic';

type Props = {
  children: ReactNode;
  isCrawler?: boolean;
  routeName?: string;
  userId?: number;
  wafTrafficMetadata?: WafTrafficMetadata;
};

export const AnalyticsContext = createContext({
  isInitialized: false,
  anonymousId: '',
});

export const AnalyticsProvider = ({
  children,
  isCrawler = false,
  routeName = '',
  userId,
  wafTrafficMetadata = EMPTY_WAF_TRAFFIC_METADATA,
}: Props): ReactElement => {
  // Set this during render so AnalyticsPageTracker, a child component, has the
  // SSR signal and authenticated ID before its initial page effect runs. This
  // is logging-only; neither value changes whether Segment is initialized.
  setWafTrafficMetadata(wafTrafficMetadata);
  setAnalyticsUserId(userId);

  const getAnonymousId = (): string =>
    Cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE_NAME) || getSegmentAnonymousId() || '';

  const hasTrackedLikelyAutomatedBrowser = useRef(false);
  const hasInitializedSegment = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [anonymousId, setAnonymous] = useState<string>(getAnonymousId());
  const [analytics, setAnalytics] = useState<Analytics | null>(null);

  useEffect(() => {
    if (isCrawler || hasInitializedSegment.current) {
      return;
    }

    // Install the one-time listener before normal analytics activity begins.
    // It observes click, pointer, touch, and scroll engagement, including on
    // mobile devices, but does not block or delay Segment.
    initializeTrustedInteractionTracking();

    // This guard an addition to the inline-script guard as a defense in depth:
    // Provider must not load segment if the browser is controlled by WebDriver.
    if (typeof navigator !== 'undefined' && navigator.webdriver === true) {
      return;
    }

    hasInitializedSegment.current = true;

    let isMounted = true;

    void loadAnalytics()
      .then((instance) => {
        if (!isMounted) {
          return;
        }

        setAnalytics(instance);
        setIsInitialized(true);
        setAnonymous(getAnonymousId());

        if (!hasTrackedLikelyAutomatedBrowser.current && isLikelyAutomatedBrowser()) {
          hasTrackedLikelyAutomatedBrowser.current = true;
          trackEvent(
            BROWSER_AUTOMATION_SIGNALS_OBSERVED_EVENT_NAME,
            getLikelyAutomatedBrowserEventProperties(routeName)
          );
        }
      })
      .catch((error: unknown) => {
        // Segment is an optional third-party dependency. Browsers can reject
        // its settings request because of privacy tools or transient network
        // failures; that must not become an application error.
        console.warn('Segment Analytics unavailable:', error);
      });

    return (): void => {
      isMounted = false;
    };
  }, [isCrawler, routeName]);

  useEffect(() => {
    if (isInitialized && analytics) {
      try {
        void analytics
          .addDestinationMiddleware('Google AdWords New', ({ payload, next }) => {
            if (['track', 'page'].includes(payload.obj.type)) {
              const traits = analytics.user().traits();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const orderCount = Number(traits.order_count as string);
              const props = payload.obj.properties ?? {};
              const value = props.value ?? props.revenue ?? props.total ?? props.booking_value;
              const transactionId =
                props.transaction_id ??
                props.order_id ??
                props.orderId ??
                props.cart_id ??
                props.cartId ??
                props.id;
              let newCustomer;

              if (!isNaN(orderCount)) {
                newCustomer = orderCount > 0;
              }

              payload.obj.properties = {
                ...props,
                new_customer: newCustomer ?? null, // Explicit null instead of undefined
                brand: payload.obj.context?.brand || 'unknown',
                value: value ?? null, // Default to null if all falsy
                transaction_id: transactionId || `temp_${Date.now()}`, // Fallback ID
              };
            }

            next(payload);
          })
          .catch((error: unknown) => {
            console.error('Error adding destination middleware:', error);
            Sentry.captureException(error);
          });
      } catch (error) {
        console.error('Error adding destination middleware:', error);
        Sentry.captureException(error);
      }
    }
  }, [analytics, isInitialized]);

  return (
    <AnalyticsContext.Provider value={{ isInitialized, anonymousId }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
